import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

export interface ContextMenuOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

export interface ContextMenuProps {
  options: ContextMenuOption[];
  position: { x: number; y: number } | null;
  onClose: () => void;
  className?: string;
}

export function ContextMenu({ options, position, onClose, className }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (position) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [position, onClose]);

  if (!position) return null;

  return (
    <div
      ref={menuRef}
      className={cn(
        'fixed z-50 min-w-[200px] rounded-md border bg-white shadow-lg',
        'animate-in fade-in-0 zoom-in-95',
        className
      )}
      style={{
        left: position.x,
        top: position.y
      }}
    >
      <div className='py-1'>
        {options.map(option => (
          <button
            key={option.id}
            onClick={() => {
              if (!option.disabled) {
                option.onClick();
                onClose();
              }
            }}
            disabled={option.disabled}
            className={cn(
              'flex w-full items-center gap-3 px-3 py-3 text-sm text-gray-700',
              'hover:bg-gray-100 focus:bg-gray-100 focus:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'transition-colors duration-150'
            )}
          >
            <div className='flex h-4 w-4 items-center justify-center text-gray-500'>{option.icon}</div>
            <span className='flex items-center'>{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
